import { Component, Element, Prop, State, h, Listen, Method } from '@stencil/core';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
type TooltipAlignment = 'start' | 'center' | 'end';

interface PositionResult {
  x: number;
  y: number;
  placement: TooltipPosition;
  arrowX: number;
  arrowY: number;
  maxWidth?: number;
  maxHeight?: number;
}

/**
 * The `tooltip-component` component is a UI component that displays a tooltip.
 * It uses a portal pattern to render the tooltip content to the document body,
 * ensuring proper positioning even when inside transformed containers or shadow DOM.
 *
 * @part tc__container - The tooltip container.
 * @part tc__content - The tooltip content.
 */
@Component({
  tag: 'tooltip-component',
  styleUrl: 'tooltip-component.scss',
  shadow: true,
})
export class TooltipComponent {
  @Element() el!: HTMLElement;

  /**
   * The preferred position of the tooltip.
   * The tooltip will attempt to use this position but will automatically adjust if it would be cut off.
   */
  @Prop() position: TooltipPosition = 'bottom';

  /**
   * The alignment of the tooltip relative to the trigger.
   */
  @Prop() alignment: TooltipAlignment = 'center';

  /**
   * The offset distance from the trigger element in pixels.
   */
  @Prop() offset: number = 8;

  /**
   * The boundary element for collision detection. Defaults to viewport.
   */
  @Prop() boundaryPadding: number = 8;

  /**
   * Whether to show the tooltip arrow.
   */
  @Prop() showArrow: boolean = true;

  /**
   * Delay in milliseconds before showing the tooltip on hover.
   */
  @Prop() showDelay: number = 0;

  /**
   * Delay in milliseconds before hiding the tooltip on mouse leave.
   */
  @Prop() hideDelay: number = 0;

  /**
   * Controls the visibility of the tooltip.
   */
  @State() isVisible = false;

  /**
   * Tracks if the tooltip is pinned open via click.
   */
  @State() isPinned = false;

  /**
   * The actual position being used after collision detection.
   */
  @State() actualPosition: TooltipPosition = this.position;

  private portalContainer: HTMLDivElement | null = null;
  private tooltipContentEl: HTMLDivElement | null = null;
  private triggerEl: HTMLElement | null = null;
  private tooltipId = `tooltip-${Math.random().toString(36).substr(2, 9)}`;
  private showTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private hideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private rafId: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private scrollParents: Element[] = [];

  /**
   * Listen for Escape key to close tooltip
   */
  @Listen('keydown', { target: 'window' })
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isVisible) {
      this.isPinned = false;
      this.isVisible = false;
      this.hidePortal();
    }
  }

  /**
   * Listen for clicks outside to close pinned tooltip
   */
  @Listen('click', { target: 'document', capture: true })
  handleDocumentClick(event: MouseEvent) {
    if (!this.isPinned || !this.isVisible) {
      return;
    }

    const path = event.composedPath();
    const clickedInside = path.includes(this.el) || (this.portalContainer && path.includes(this.portalContainer));
    if (!clickedInside) {
      this.isPinned = false;
      this.isVisible = false;
      this.hidePortal();
    }
  }

  /**
   * Listen for scroll events to update position
   */
  @Listen('scroll', { target: 'window', capture: true, passive: true })
  handleScroll() {
    if (this.isVisible && this.portalContainer) {
      this.updatePosition();
    }
  }

  /**
   * Listen for resize events to update position
   */
  @Listen('resize', { target: 'window', passive: true })
  handleResize() {
    if (this.isVisible && this.portalContainer) {
      this.updatePosition();
    }
  }

  componentDidLoad() {
    this.triggerEl = this.el.shadowRoot?.querySelector('.info-icon') as HTMLElement;
  }

  disconnectedCallback() {
    this.cleanup();
  }

  /**
   * Programmatically show the tooltip
   */
  @Method()
  async show() {
    this.showTooltip();
  }

  /**
   * Programmatically hide the tooltip
   */
  @Method()
  async hide() {
    this.isPinned = false;
    this.isVisible = false;
    this.hidePortal();
  }

  /**
   * Clean up all resources
   */
  private cleanup() {
    if (this.showTimeoutId) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    this.removeScrollListeners();
    this.hidePortal();
  }

  /**
   * Get all scrollable parent elements
   */
  private getScrollParents(element: Element): Element[] {
    const scrollParents: Element[] = [];
    let current: Element | null = element.parentElement;

    while (current) {
      const style = getComputedStyle(current);
      const overflowY = style.overflowY;
      const overflowX = style.overflowX;

      if (overflowY === 'auto' || overflowY === 'scroll' || overflowX === 'auto' || overflowX === 'scroll') {
        scrollParents.push(current);
      }
      current = current.parentElement;
    }

    return scrollParents;
  }

  /**
   * Add scroll listeners to all scrollable parents
   */
  private addScrollListeners() {
    this.scrollParents = this.getScrollParents(this.el);
    this.scrollParents.forEach(parent => {
      parent.addEventListener('scroll', this.handleParentScroll, { passive: true });
    });
  }

  /**
   * Remove scroll listeners from all scrollable parents
   */
  private removeScrollListeners() {
    this.scrollParents.forEach(parent => {
      parent.removeEventListener('scroll', this.handleParentScroll);
    });
    this.scrollParents = [];
  }

  private handleParentScroll = () => {
    if (this.isVisible && this.portalContainer) {
      this.updatePosition();
    }
  };

  /**
   * Create the portal container in document.body
   */
  private createPortal() {
    if (this.portalContainer) return;

    this.portalContainer = document.createElement('div');
    this.portalContainer.className = 'tooltip-portal';
    this.portalContainer.setAttribute('data-tooltip-id', this.tooltipId);
    this.portalContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      pointer-events: none;
      z-index: 99999;
    `;

    // Create tooltip content element
    this.tooltipContentEl = document.createElement('div');
    this.tooltipContentEl.id = this.tooltipId;
    this.tooltipContentEl.setAttribute('role', 'tooltip');
    this.tooltipContentEl.setAttribute('aria-hidden', 'true');
    this.tooltipContentEl.className = 'tooltip-content';

    // Apply styles
    this.applyTooltipStyles();

    this.portalContainer.appendChild(this.tooltipContentEl);
    document.body.appendChild(this.portalContainer);

    // Setup observers for dynamic content
    this.setupObservers();
  }

  /**
   * Apply comprehensive styles to the portal tooltip
   */
  private applyTooltipStyles() {
    if (!this.tooltipContentEl) return;

    // Get CSS custom properties from the component
    const computedStyle = getComputedStyle(this.el);
    const baseColor = computedStyle.getPropertyValue('--nylas-base-800').trim() || '#1f2937';
    const textColor = computedStyle.getPropertyValue('--nylas-base-0').trim() || '#ffffff';
    const fontFamily = computedStyle.getPropertyValue('--nylas-font-family').trim() || 'inherit';
    const borderRadius = computedStyle.getPropertyValue('--nylas-border-radius').trim() || '6px';

    this.tooltipContentEl.style.cssText = `
      position: absolute;
      background-color: ${baseColor};
      color: ${textColor};
      font-family: ${fontFamily};
      text-align: left;
      border-radius: ${borderRadius};
      padding: 0.5rem 1rem;
      max-width: 300px;
      width: max-content;
      font-size: 14px;
      line-height: 1.4;
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      transition: opacity 0.15s ease-out, visibility 0.15s ease-out;
      box-sizing: border-box;
    `;
  }

  /**
   * Setup observers for content changes
   */
  private setupObservers() {
    // Observe trigger element for size/position changes
    if (this.triggerEl && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.isVisible) {
          this.updatePosition();
        }
      });
      this.resizeObserver.observe(this.triggerEl);
    }
  }

  /**
   * Update portal content from slot
   */
  private updatePortalContent() {
    if (!this.tooltipContentEl) return;

    const slot = this.el.shadowRoot?.querySelector('slot[name="tooltip-content"]') as HTMLSlotElement;
    if (slot) {
      const assignedNodes = slot.assignedNodes({ flatten: true });
      if (assignedNodes.length > 0) {
        // Clone slotted content
        this.tooltipContentEl.innerHTML = '';
        assignedNodes.forEach(node => {
          this.tooltipContentEl!.appendChild(node.cloneNode(true));
        });
      } else {
        this.tooltipContentEl.textContent = 'This is a help text.';
      }
    } else {
      this.tooltipContentEl.textContent = 'This is a help text.';
    }
  }

  /**
   * Calculate the best position for the tooltip
   */
  private calculatePosition(): PositionResult {
    if (!this.triggerEl || !this.tooltipContentEl) {
      return { x: 0, y: 0, placement: this.position, arrowX: 0, arrowY: 0 };
    }

    const triggerRect = this.triggerEl.getBoundingClientRect();
    const tooltipRect = this.tooltipContentEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = this.boundaryPadding;
    const offset = this.offset;

    // Calculate available space in each direction
    const spaceAbove = triggerRect.top - padding;
    const spaceBelow = viewportHeight - triggerRect.bottom - padding;
    const spaceLeft = triggerRect.left - padding;
    const spaceRight = viewportWidth - triggerRect.right - padding;

    // Determine the best placement
    let placement = this.position;
    const tooltipHeight = tooltipRect.height || 40; // Fallback estimate
    const tooltipWidth = tooltipRect.width || 200; // Fallback estimate

    // Check if preferred position fits, otherwise flip
    const fits = {
      top: spaceAbove >= tooltipHeight + offset,
      bottom: spaceBelow >= tooltipHeight + offset,
      left: spaceLeft >= tooltipWidth + offset,
      right: spaceRight >= tooltipWidth + offset,
    };

    if (!fits[placement]) {
      // Try opposite side first
      const opposites: Record<TooltipPosition, TooltipPosition> = {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left',
      };
      const opposite = opposites[placement];

      if (fits[opposite]) {
        placement = opposite;
      } else {
        // Find the direction with most space
        const spaces: Record<TooltipPosition, number> = {
          top: spaceAbove,
          bottom: spaceBelow,
          left: spaceLeft,
          right: spaceRight,
        };
        placement = Object.entries(spaces).sort(([, a], [, b]) => b - a)[0][0] as TooltipPosition;
      }
    }

    // Calculate coordinates based on placement
    let x = 0;
    let y = 0;
    let arrowX = 0;
    let arrowY = 0;
    const arrowSize = 6;

    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;

    switch (placement) {
      case 'top':
        x = triggerCenterX - tooltipWidth / 2;
        y = triggerRect.top - tooltipHeight - offset;
        arrowX = tooltipWidth / 2;
        arrowY = tooltipHeight;
        break;
      case 'bottom':
        x = triggerCenterX - tooltipWidth / 2;
        y = triggerRect.bottom + offset;
        arrowX = tooltipWidth / 2;
        arrowY = -arrowSize * 2;
        break;
      case 'left':
        x = triggerRect.left - tooltipWidth - offset;
        y = triggerCenterY - tooltipHeight / 2;
        arrowX = tooltipWidth;
        arrowY = tooltipHeight / 2;
        break;
      case 'right':
        x = triggerRect.right + offset;
        y = triggerCenterY - tooltipHeight / 2;
        arrowX = -arrowSize * 2;
        arrowY = tooltipHeight / 2;
        break;
    }

    // Apply boundary constraints (shift if necessary)
    let shiftX = 0;
    let shiftY = 0;

    if (x < padding) {
      shiftX = padding - x;
      x = padding;
    } else if (x + tooltipWidth > viewportWidth - padding) {
      shiftX = viewportWidth - padding - tooltipWidth - x;
      x = viewportWidth - padding - tooltipWidth;
    }

    if (y < padding) {
      shiftY = padding - y;
      y = padding;
    } else if (y + tooltipHeight > viewportHeight - padding) {
      shiftY = viewportHeight - padding - tooltipHeight - y;
      y = viewportHeight - padding - tooltipHeight;
    }

    // Adjust arrow position to compensate for shift
    if (placement === 'top' || placement === 'bottom') {
      arrowX -= shiftX;
      // Clamp arrow position to stay within tooltip
      arrowX = Math.max(arrowSize + 4, Math.min(tooltipWidth - arrowSize - 4, arrowX));
    } else {
      arrowY -= shiftY;
      // Clamp arrow position to stay within tooltip
      arrowY = Math.max(arrowSize + 4, Math.min(tooltipHeight - arrowSize - 4, arrowY));
    }

    return {
      x: Math.round(x),
      y: Math.round(y),
      placement,
      arrowX: Math.round(arrowX),
      arrowY: Math.round(arrowY),
      maxWidth: Math.min(300, viewportWidth - padding * 2),
      maxHeight: viewportHeight - padding * 2,
    };
  }

  /**
   * Update tooltip position
   */
  private updatePosition() {
    if (!this.tooltipContentEl || !this.portalContainer) return;

    const position = this.calculatePosition();
    this.actualPosition = position.placement;

    this.tooltipContentEl.style.transform = `translate(${position.x}px, ${position.y}px)`;
    this.tooltipContentEl.style.maxWidth = `${position.maxWidth}px`;

    // Update arrow
    this.updateArrow(position);
  }

  /**
   * Update arrow position and style
   */
  private updateArrow(position: PositionResult) {
    if (!this.tooltipContentEl || !this.showArrow) return;

    // Remove existing arrow
    const existingArrow = this.tooltipContentEl.querySelector('.tooltip-arrow');
    if (existingArrow) {
      existingArrow.remove();
    }

    // Get colors
    const computedStyle = getComputedStyle(this.el);
    const baseColor = computedStyle.getPropertyValue('--nylas-base-800').trim() || '#1f2937';

    // Create arrow
    const arrow = document.createElement('div');
    arrow.className = 'tooltip-arrow';
    arrow.style.cssText = `
      position: absolute;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 6px;
    `;

    switch (position.placement) {
      case 'top':
        arrow.style.bottom = '-12px';
        arrow.style.left = `${position.arrowX - 6}px`;
        arrow.style.borderColor = `${baseColor} transparent transparent transparent`;
        break;
      case 'bottom':
        arrow.style.top = '-12px';
        arrow.style.left = `${position.arrowX - 6}px`;
        arrow.style.borderColor = `transparent transparent ${baseColor} transparent`;
        break;
      case 'left':
        arrow.style.right = '-12px';
        arrow.style.top = `${position.arrowY - 6}px`;
        arrow.style.borderColor = `transparent transparent transparent ${baseColor}`;
        break;
      case 'right':
        arrow.style.left = '-12px';
        arrow.style.top = `${position.arrowY - 6}px`;
        arrow.style.borderColor = `transparent ${baseColor} transparent transparent`;
        break;
    }

    this.tooltipContentEl.appendChild(arrow);
  }

  /**
   * Show the tooltip portal
   */
  private showPortal() {
    this.createPortal();
    this.updatePortalContent();
    this.addScrollListeners();

    // Use RAF to ensure content is rendered before measuring
    this.rafId = requestAnimationFrame(() => {
      this.rafId = requestAnimationFrame(() => {
        this.updatePosition();

        if (this.tooltipContentEl) {
          this.tooltipContentEl.style.opacity = '1';
          this.tooltipContentEl.style.visibility = 'visible';
          this.tooltipContentEl.setAttribute('aria-hidden', 'false');
        }
      });
    });
  }

  /**
   * Hide the tooltip portal
   */
  private hidePortal() {
    if (this.tooltipContentEl) {
      this.tooltipContentEl.style.opacity = '0';
      this.tooltipContentEl.style.visibility = 'hidden';
      this.tooltipContentEl.setAttribute('aria-hidden', 'true');
    }

    this.removeScrollListeners();

    // Clean up portal after transition
    setTimeout(() => {
      if (!this.isVisible && this.portalContainer) {
        this.portalContainer.remove();
        this.portalContainer = null;
        this.tooltipContentEl = null;
      }
    }, 150);
  }

  /**
   * Show the tooltip with delay
   */
  private showTooltip() {
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }

    if (this.showDelay > 0) {
      this.showTimeoutId = setTimeout(() => {
        this.isVisible = true;
        this.showPortal();
      }, this.showDelay);
    } else {
      this.isVisible = true;
      this.showPortal();
    }
  }

  /**
   * Hide the tooltip with delay
   */
  private hideTooltip() {
    if (this.isPinned) return;

    if (this.showTimeoutId) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }

    if (this.hideDelay > 0) {
      this.hideTimeoutId = setTimeout(() => {
        this.isVisible = false;
        this.hidePortal();
      }, this.hideDelay);
    } else {
      this.isVisible = false;
      this.hidePortal();
    }
  }

  /**
   * Handle click to pin/unpin tooltip
   */
  private handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.isPinned = !this.isPinned;

    if (this.isPinned) {
      this.showTooltip();
    } else {
      this.isVisible = false;
      this.hidePortal();
    }
  };

  /**
   * Handle mouse enter
   */
  private handleMouseEnter = () => {
    if (!this.isPinned) {
      this.showTooltip();
    }
  };

  /**
   * Handle mouse leave
   */
  private handleMouseLeave = () => {
    this.hideTooltip();
  };

  /**
   * Handle focus
   */
  private handleFocus = () => {
    if (!this.isPinned) {
      this.showTooltip();
    }
  };

  /**
   * Handle blur
   */
  private handleBlur = () => {
    this.hideTooltip();
  };

  render() {
    return (
      <div class="tooltip-container" part="tc__container">
        <span
          class="info-icon"
          tabindex="0"
          role="button"
          aria-describedby={this.tooltipId}
          aria-expanded={this.isVisible ? 'true' : 'false'}
          aria-pressed={this.isPinned ? 'true' : 'false'}
          onClick={this.handleClick}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
        >
          <slot name="tooltip-icon">i</slot>
        </span>
        {/* Hidden slot for content - actual rendering happens in portal */}
        <div style={{ display: 'none' }}>
          <slot name="tooltip-content">This is a help text.</slot>
        </div>
        {/* Hidden element for screen readers and tests that need ARIA attributes */}
        <span
          id={this.tooltipId}
          role="tooltip"
          aria-hidden={!this.isVisible ? 'true' : 'false'}
          class={{
            'tooltip-content': true,
            'tooltip-visible': this.isVisible,
            [this.actualPosition]: true,
          }}
          part="tc__content"
          style={{ display: 'none' }}
        >
          <slot name="tooltip-content-aria">
            <slot name="tooltip-content">This is a help text.</slot>
          </slot>
        </span>
      </div>
    );
  }
}
